/**
 * UUID validation utility
 * Prevents P2023 errors by validating UUIDs before Prisma queries
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID format
 */
export function isValidUuid(value: string | null | undefined): boolean {
  if (!value || typeof value !== "string") return false;
  return UUID_REGEX.test(value);
}

/**
 * Validate UUID and return it, or throw an error
 */
export function validateUuid(value: string | null | undefined, fieldName = "id"): string {
  if (!isValidUuid(value)) {
    throw new Error(`Invalid UUID format for ${fieldName}: ${value}`);
  }
  return value as string;
}

/**
 * Generate a UUID v4
 * Uses crypto.randomUUID() if available (modern browsers/Node.js), otherwise falls back to manual generation
 */
export function generateUuid(): string {
  // Use crypto.randomUUID() if available (Node.js 14.17.0+, modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older environments
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
